import { Inject, Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import ApiService from '../api.service.abstract';
import {
  HotelbedsProperty,
  NewAccommodation,
  RoomCodeDetail,
  HotelbedsCachePricing,
  HotelbedsCacheQuotationPricing,
  HotelbedsContract,
  CheapestContractResponse,
  BestRatesResponse,
} from '@pulpo/pulpo-models';
import { APP_CONFIG } from '@pulpo/app-config';
import { RouteReuseStrategy } from '@angular/router';
import { Observable } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class NewAccommodationHotelbedsService extends ApiService<NewAccommodation> {
  constructor(
    @Inject(APP_CONFIG) environment: any,
    protected http: HttpClient,
    routeReuse: RouteReuseStrategy
  ) {
    super(
      environment,
      http,
      'accommodation/api/accommodations/hotelbeds',
      routeReuse
    );
    if (environment.name == 'localDev') {
      this.url = 'http://localhost:8088';
    }
  }

  public getPropertiesByCountryAndCity(
    countryCode: string,
    city: string
  ): Observable<HttpResponse<HotelbedsProperty[]>> {
    return this.http.get<HotelbedsProperty[]>(
      `${this.url}/${this.endpoint}/properties?countryCode=${countryCode}&city=${city}&hasHotels=true`,
      {
        observe: 'response',
      }
    );
  }

  public getPropertiesByPartialName(
    countryCode: string,
    partialName: string
  ): Observable<HttpResponse<HotelbedsProperty[]>> {
    return this.http.get<HotelbedsProperty[]>(
      `${this.url}/${this.endpoint}/properties/search?countryCode=${countryCode}&partialName=${partialName}&hasHotels=true`,
      {
        observe: 'response',
      }
    );
  }

  public getCitiesByCountry(
    countryCode: string
  ): Observable<HttpResponse<string[]>> {
    return this.http.get<string[]>(
      `${this.url}/${this.endpoint}/cities-by-country-code?countryCode=${countryCode}`,
      {
        observe: 'response',
      }
    );
  }

  public getCountries(): Observable<HttpResponse<string[]>> {
    return this.http.get<string[]>(`${this.url}/${this.endpoint}/countries`, {
      observe: 'response',
    });
  }

  public getCalendarAvailability(
    propertyCode: string,
    year: number,
    month: number,
    room: string,
    board: string
  ): Observable<HttpResponse<any[]>> {
    return this.http.get<any[]>(
      `${this.url}/${this.endpoint}/calendar-availability?property-code=${propertyCode}&year=${year}&month=${month}&room=${room}&board=${board}`,
      {
        observe: 'response',
      }
    );
  }

  public getDistinctRoomCodesByContract(
    contractId: string
  ): Observable<HttpResponse<RoomCodeDetail[]>> {
    return this.http.get<RoomCodeDetail[]>(
      `${this.url}/${this.endpoint}/distinct-room-codes?contract-id=${contractId}`,
      {
        observe: 'response',
      }
    );
  }

  public getCalendarPrices(
    propertyCode: string,
    roomCode: string,
    board: string,
    startDate: string,
    endDate: string
  ): Observable<HttpResponse<HotelbedsCachePricing[]>> {
    return this.http.get<HotelbedsCachePricing[]>(
      `${this.url}/${this.endpoint}/calendar-request?property-code=${propertyCode}&room=${roomCode}&board=${board}&start-date=${startDate}&end-date=${endDate}`,
      {
        observe: 'response',
      }
    );
  }

  public getContractsFromProperty(
    propertyCode: string
  ): Observable<HttpResponse<HotelbedsContract[]>> {
    return this.http.get<HotelbedsContract[]>(
      `${this.url}/${this.endpoint}/quoter/contracts/${propertyCode}`,
      {
        observe: 'response',
      }
    );
  }

  public getCalendarPricesV2(
    contractId: string,
    roomCode: string,
    board: string,
    startDate: string,
    endDate: string,
    adults: number = 2
  ): Observable<HttpResponse<HotelbedsCacheQuotationPricing[]>> {
    return this.http.post<HotelbedsCacheQuotationPricing[]>(
      `${this.url}/${this.endpoint}/quoter/range/${contractId}`,
      {
        room_type: roomCode.split('.')[0],
        characteristic: roomCode.split('.')[1],
        board: board,
        from_date: startDate,
        to_date: endDate,
        adults: adults,
        booking_date: new Date().toISOString().split('T')[0],
      },
      {
        observe: 'response',
      }
    );
  }

  public exportCachePricesToExcel(
    contractId: string,
    roomCode: string,
    board: string,
    startDate: string,
    endDate: string,
    adults: number = 2
  ): Observable<HttpResponse<Blob>> {
    return this.http.post(
      `${this.url}/${this.endpoint}/quoter/range/${contractId}/export`,
      {
        room_type: roomCode.split('.')[0],
        characteristic: roomCode.split('.')[1],
        board: board,
        from_date: startDate,
        to_date: endDate,
        adults: adults,
        booking_date: new Date().toISOString().split('T')[0],
      },
      {
        observe: 'response',
        responseType: 'blob',
      }
    ) as Observable<HttpResponse<Blob>>;
  }

  public getBoardCodesByContract(
    contractId: string
  ): Observable<HttpResponse<{ code: string; name: string }[]>> {
    return this.http.get<{ code: string; name: string }[]>(
      `${this.url}/accommodation/api/accommodations/hotelbeds/parser/caches/${contractId}/board-codes`,
      {
        observe: 'response',
      }
    );
  }

  public getSingleDateQuotation(
    contractId: string,
    roomCode: string,
    board: string,
    singleDate: string,
    adults: number = 2
  ): Observable<HttpResponse<any>> {
    return this.http.post<any>(
      `${this.url}/${this.endpoint}/quoter/single/${contractId}`,
      {
        room_type: roomCode.split('.')[0],
        characteristic: roomCode.split('.')[1],
        board: board,
        single_date: singleDate,
        booking_date: new Date().toISOString().split('T')[0],
        adults: adults,
      },
      {
        observe: 'response',
      }
    );
  }

  public getRoomCodesByProperty(
    propertyCode: string
  ): Observable<HttpResponse<RoomCodeDetail[]>> {
    return this.http.get<RoomCodeDetail[]>(
      `${this.url}/${this.endpoint}/quoter/room-codes/${propertyCode}`,
      { observe: 'response' }
    );
  }

  public findBestRates(
    propertyCode: string,
    fromDate: string,
    toDate: string,
    adults: number = 2,
    nonRefundable: boolean = true,
    roomCode?: string
  ): Observable<HttpResponse<BestRatesResponse>> {
    const body: any = {
      from_date: fromDate,
      to_date: toDate,
      adults: adults,
      non_refundable: nonRefundable,
      booking_date: new Date().toISOString().split('T')[0],
    };
    if (roomCode) {
      body.room_type = roomCode.split('.')[0];
      body.characteristic = roomCode.split('.')[1];
    }
    return this.http.post<BestRatesResponse>(
      `${this.url}/${this.endpoint}/quoter/best-rates/${propertyCode}`,
      body,
      { observe: 'response' }
    );
  }

  public findCheapestContract(
    propertyId: string,
    roomCode: string,
    board: string,
    startDate: string,
    endDate: string,
    adults: number = 2
  ): Observable<HttpResponse<CheapestContractResponse>> {
    return this.http.post<CheapestContractResponse>(
      `${this.url}/${this.endpoint}/quoter/cheapest/${propertyId}`,
      {
        room_type: roomCode.split('.')[0],
        characteristic: roomCode.split('.')[1],
        board: board,
        from_date: startDate,
        to_date: endDate,
        adults: adults,
        booking_date: new Date().toISOString().split('T')[0],
      },
      { observe: 'response' }
    );
  }
}
